import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import ZohoLogo from '../../../components/ZohoLogo';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import Tooltip from '../../../components/ui/Tooltip';
import StatusBadge from './StatusBadge';
import AvailabilityLabel from './AvailabilityLabel';
import PrioritizeButton from './PrioritizeButton';
import { getCandidateProfilePath } from '../../../lib/utils/candidateRoutes';
import { getCandidatesListPathFromSearchParams } from '../../../lib/utils/candidateListRoutes';
import { Checkbox } from '../../../components/ui/Checkbox';
import { planCanPrioritize } from '../../../lib/utils/planEntitlements';

const CandidateTableRow = ({
  candidate,
  onTogglePriority,
  onDeleteCandidate,
  userPlan,
  isRestricted,
  isSelected = false,
  onToggleSelect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Normalize plan to lowercase for comparison
  const plan = (userPlan ?? '').toLowerCase();
  
  // Feature availability based on plan
  const canPrioritize = planCanPrioritize(plan);

  const handleViewProfile = () => {
    navigate(getCandidateProfilePath(candidate?.id), {
      state: { returnTo: getCandidatesListPathFromSearchParams(searchParams) },
    });
  };

  const handleDeleteClick = () => setShowDeleteConfirm(true);
  const handleDeleteConfirm = () => {
    onDeleteCandidate?.(candidate?.id);
    setShowDeleteConfirm(false);
  };

  const hasPhone = Boolean(candidate?.phone?.trim());

  // Generate initials from full name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <>
    <tr
      className="group border-b border-border cursor-pointer"
      onClick={!isRestricted ? handleViewProfile : undefined}
    >
      <td
        className="pl-4 pr-2 py-4 w-8 transition-colors group-hover:bg-muted"
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          id={`select-candidate-${candidate?.id}`}
          size="sm"
          checked={isSelected}
          onChange={() => onToggleSelect?.(candidate?.id)}
          aria-label={`Select ${candidate?.fullName || 'candidate'}`}
          className="justify-center space-x-0"
        />
      </td>
      <td className="px-4 py-4 transition-colors group-hover:bg-muted">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
            {candidate?.profileImage ? (
              <Image
                src={candidate?.profileImage}
                alt={candidate?.profileImageAlt}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                {getInitials(candidate?.fullName)}
              </div>
            )}
          </div>
          <div className="min-w-0 max-w-[250px]">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-foreground">
                {candidate?.fullName}
              </p>
              {candidate?.isNew && (
                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded flex-shrink-0">
                  New
                </span>
              )}
              {candidate?.isFromZoho && (
                <Tooltip content="Imported from Zoho Recruit" align="start" size="sm">
                  <span className="inline-flex items-center justify-center flex-shrink-0">
                    <ZohoLogo size={18} />
                  </span>
                </Tooltip>
              )}
            </div>
            <p className="text-sm text-muted-foreground break-words leading-snug">
              {candidate?.currentRole}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-4 transition-colors group-hover:bg-muted">
        <p className="text-sm text-foreground">
          {candidate?.companyName}
        </p>
      </td>
      <td className="px-4 py-4 whitespace-nowrap transition-colors group-hover:bg-muted">
        <div className="inline-flex items-center gap-1.5">
          <StatusBadge status={candidate?.status} />
          {candidate?.isManualOpenToWork && (
            <Tooltip content="Status set manually" align="start" size="sm">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-muted border border-border text-muted-foreground">
                <Icon name="Pencil" size={11} />
              </span>
            </Tooltip>
          )}
        </div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap transition-colors group-hover:bg-muted">
        <p className="text-sm text-muted-foreground">
          {candidate?.lastCheckDate}
        </p>
      </td>
      <td className="px-4 py-4 text-center transition-colors group-hover:bg-muted">
        <div className="flex items-center justify-center gap-2">
          <AvailabilityLabel type="phone" available={hasPhone} />
          <AvailabilityLabel type="linkedin" available={!candidate?.isInvalidLink} />
        </div>
      </td>
      <td className="px-4 py-4 transition-colors group-hover:bg-muted" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          {!isRestricted && canPrioritize && (
            <PrioritizeButton
              isPrioritized={candidate?.isPrioritized}
              hasPhone={hasPhone}
              onClick={() => onTogglePriority(candidate?.id)}
              showTooltip={false}
              className="flex-shrink-0"
            />
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDeleteClick}
            iconName="Trash2"
            className="flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
          >
          </Button>
        </div>
      </td>
    </tr>
    {createPortal(
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title={t('candidates.deleteCandidateTitle')}
        message={t('candidates.deleteCandidateMessage', { name: candidate?.fullName })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
      />,
      document.body
    )}
    </>
  );
};

export default CandidateTableRow;